import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FileText, Download, Upload, UserCheck, Calendar, TrendingUp, AlertTriangle, Users, Mail, Send, Printer, Sparkles, Loader2, BarChart3, Trophy, Target, Share2 } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import VoicePlayer from '@/components/VoicePlayer';
import { PacReplyView } from '@/components/PacReplyChips';
import axios from 'axios';
import { toast } from 'sonner';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import usePermissions from '@/hooks/usePermissions';
import { ReadOnlyBanner, NoAccessScreen } from '@/components/PermissionUI';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ReportsPage({ user, onLogout, permissions }) {
  const { canView, isReadOnly } = usePermissions(permissions);
  const navigate = useNavigate();

  const postRecapToSocial = () => {
    if (!recapText) return;
    navigate(`/social-media?draft=${encodeURIComponent(recapText)}&source=recap`);
  };
  const [players, setPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState('');
  const [reportData, setReportData] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  
  // Attendance report state
  const [attendanceReport, setAttendanceReport] = useState(null);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [isLoadingAttendance, setIsLoadingAttendance] = useState(false);
  const [isSendingAlerts, setIsSendingAlerts] = useState(false);
  
  // Scouting reports and box scores state
  const [scoutingReports, setScoutingReports] = useState([]);
  const [gameSessions, setGameSessions] = useState([]);
  const [selectedBoxScore, setSelectedBoxScore] = useState(null);
  const [boxScoreData, setBoxScoreData] = useState(null);
  const [boxScoreViewMode, setBoxScoreViewMode] = useState('totals'); // 'totals' or 'quarters'
  const [recapText, setRecapText] = useState(null);
  const [showRecapDialog, setShowRecapDialog] = useState(false);
  const [isGeneratingRecap, setIsGeneratingRecap] = useState(false);
  const [showScoutDialog, setShowScoutDialog] = useState(false);
  const [newScout, setNewScout] = useState({ opponent_name: '', strengths: '', weaknesses: '', game_plan: '' });
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [aiScoutDialog, setAiScoutDialog] = useState(false);
  const [aiScoutRequest, setAiScoutRequest] = useState({ 
    opponent_name: '', 
    opponent_info: '', 
    our_strengths: '',
    areas_to_exploit: ''
  });
  const [showEmailReportDialog, setShowEmailReportDialog] = useState(false);
  const [emailReportRecipient, setEmailReportRecipient] = useState('');
  const [isSendingReport, setIsSendingReport] = useState(false);

  // Compare Players state
  const [comparePlayer1, setComparePlayer1] = useState(null);
  const [comparePlayer2, setComparePlayer2] = useState(null);
  const [player1Stats, setPlayer1Stats] = useState(null);
  const [player2Stats, setPlayer2Stats] = useState(null);
  const [isLoadingComparison, setIsLoadingComparison] = useState(false);

  // Season Trends state
  const [seasonTrends, setSeasonTrends] = useState(null);
  const [isLoadingTrends, setIsLoadingTrends] = useState(false);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv']
    },
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setUploadFile(acceptedFiles[0]);
      }
    }
  });

  useEffect(() => {
    loadPlayers();
    loadAttendanceReport();
    loadScoutingReports();
    loadGameSessions();
    loadSeasonTrends();
  }, []);

  const loadPlayers = async () => {
    try {
      const response = await axios.get(`${API}/players?team_id=${user.team_id}`);
      setPlayers(response.data);
    } catch (error) {
      toast.error('Failed to load players');
    }
  };

  const loadSeasonTrends = async () => {
    setIsLoadingTrends(true);
    try {
      const response = await axios.get(`${API}/reports/season-trends?team_id=${user.team_id}`);
      setSeasonTrends(response.data);
    } catch (error) {
      console.error('Failed to load season trends');
    } finally {
      setIsLoadingTrends(false);
    }
  };

  const loadScoutingReports = async () => {
    try {
      const response = await axios.get(`${API}/scouting-reports?team_id=${user.team_id}`);
      setScoutingReports(response.data);
    } catch (error) {
      console.error('Failed to load scouting reports');
    }
  };

  const loadGameSessions = async () => {
    try {
      const response = await axios.get(`${API}/game-day-stats/sessions?team_id=${user.team_id}`);
      setGameSessions(response.data.filter(s => s.status === 'completed'));
    } catch (error) {
      console.error('Failed to load game sessions');
    }
  };

  const generateRecap = async (regenerate = false) => {
    if (!selectedBoxScore) return;
    setIsGeneratingRecap(true);
    setShowRecapDialog(true);
    try {
      const response = await axios.post(`${API}/game-day-stats/recap/${selectedBoxScore.id}?regenerate=${regenerate}`);
      setRecapText(response.data.recap);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate recap');
      setShowRecapDialog(false);
    } finally {
      setIsGeneratingRecap(false);
    }
  };

  const copyRecap = () => {
    navigator.clipboard.writeText(recapText || '');
    toast.success('Recap copied to clipboard!');
  };

  const loadBoxScore = async (session) => {
    try {
      const response = await axios.get(`${API}/game-day-stats/summary/${session.id}`);
      setBoxScoreData(response.data);
      setSelectedBoxScore(session);
    } catch (error) {
      toast.error('Failed to load box score');
    }
  };

  // Compare Players functions
  const loadPlayerStats = async (playerId, setStats) => {
    try {
      const response = await axios.get(`${API}/reports/player-stats/${playerId}`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load player stats');
      setStats(null);
    }
  };

  const handleComparePlayer1Change = async (playerId) => {
    setComparePlayer1(playerId);
    if (playerId) {
      setIsLoadingComparison(true);
      await loadPlayerStats(playerId, setPlayer1Stats);
      setIsLoadingComparison(false);
    } else {
      setPlayer1Stats(null);
    }
  };

  const handleComparePlayer2Change = async (playerId) => {
    setComparePlayer2(playerId);
    if (playerId) {
      setIsLoadingComparison(true);
      await loadPlayerStats(playerId, setPlayer2Stats);
      setIsLoadingComparison(false);
    } else {
      setPlayer2Stats(null);
    }
  };

  // Get stat comparison color (who's better)
  const getComparisonColor = (stat1, stat2, higherIsBetter = true) => {
    if (stat1 === stat2) return 'text-muted-foreground';
    if (higherIsBetter) {
      return stat1 > stat2 ? 'text-green-600 font-bold' : 'text-red-600';
    } else {
      return stat1 < stat2 ? 'text-green-600 font-bold' : 'text-red-600';
    }
  };

  const handleAddScoutReport = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/scouting-reports`, { ...newScout, team_id: user.team_id });
      toast.success('Scouting report created!');
      setShowScoutDialog(false);
      setNewScout({ opponent_name: '', strengths: '', weaknesses: '', game_plan: '' });
      loadScoutingReports();
    } catch (error) {
      toast.error('Failed to create scouting report');
    }
  };

  const loadAttendanceReport = async () => {
    setIsLoadingAttendance(true);
    try {
      const response = await axios.get(`${API}/reports/attendance?team_id=${user.team_id}&start_date=${dateRange.start}&end_date=${dateRange.end}`);
      setAttendanceReport(response.data);
      setAttendanceHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to load attendance report');
    } finally {
      setIsLoadingAttendance(false);
    }
  };

  const generateReport = async () => {
    if (!selectedPlayer) {
      toast.error('Please select a player');
      return;
    }

    try {
      const response = await axios.get(`${API}/reports/player-stats/${selectedPlayer}`);
      setReportData(response.data);
      toast.success('Report generated successfully');
    } catch (error) {
      toast.error('Failed to generate report');
    }
  };

  const handleUploadReport = async () => {
    if (!uploadFile) {
      toast.error('Please select a file to upload');
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('team_id', user.team_id);

    try {
      await axios.post(`${API}/reports/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Report uploaded successfully!');
      setShowUploadDialog(false);
      setUploadFile(null);
    } catch (error) {
      toast.error('Failed to upload report');
    }
  };

  const exportAttendanceCSV = () => {
    if (!attendanceReport?.player_stats) return;
    
    const headers = ['Player', 'Jersey #', 'Present', 'Absent', 'Total Practices', 'Attendance %'];
    const rows = attendanceReport.player_stats.map(p => [
      `${p.first_name} ${p.last_name}`,
      p.jersey_number || 'N/A',
      p.present_count,
      p.absent_count,
      p.total_practices,
      `${p.attendance_percentage}%`
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance_report_${dateRange.start}_to_${dateRange.end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Attendance report exported!');
  };

  const sendAttendanceAlerts = async () => {
    if (!attendanceReport?.players_below_75 || attendanceReport.players_below_75 === 0) {
      toast.info('No players below 75% attendance to alert');
      return;
    }

    setIsSendingAlerts(true);
    try {
      const response = await axios.post(`${API}/reports/attendance/send-alerts`, {
        team_id: user.team_id,
        threshold: 75,
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      
      if (response.data.email_configured) {
        toast.success(`Attendance alerts sent to ${response.data.emails_sent} parent(s)!`);
      } else {
        toast.info(`Alerts logged for ${response.data.players_alerted?.length || 0} player(s). Configure RESEND_API_KEY for email delivery.`);
      }
    } catch (error) {
      toast.error('Failed to send attendance alerts');
    } finally {
      setIsSendingAlerts(false);
    }
  };

  const getAttendanceColor = (percentage) => {
    if (percentage >= 90) return 'text-green-600 bg-green-100';
    if (percentage >= 75) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  // Helper to render text with bullet points
  const handleDownloadScoutingReport = (report) => {
    const opponent = report.opponent || report.opponent_name || 'Opponent';
    const dateStr = report.created_at ? new Date(report.created_at).toLocaleDateString() : '';
    const lines = [`SCOUTING REPORT — vs ${opponent}`, `Created: ${dateStr}`, ''];

    if (report.ai_generated && Array.isArray(report.structured?.paragraphs) && report.structured.paragraphs.length > 0) {
      report.structured.paragraphs.forEach((p) => {
        lines.push(`[${p.label}]`, p.text, '');
      });
      if (report.structured.citations?.length > 0) {
        lines.push('Sources: ' + report.structured.citations.map((c) => c.ref).join(', '));
      }
    } else if (report.ai_generated && report.content) {
      lines.push(report.content);
    } else {
      if (report.strengths) lines.push('STRENGTHS:', report.strengths, '');
      if (report.weaknesses) lines.push('WEAKNESSES:', report.weaknesses, '');
      if (report.game_plan) lines.push('GAME PLAN:', report.game_plan, '');
    }

    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scouting_report_vs_${opponent.replace(/\s+/g, '_')}_${(report.created_at || '').split('T')[0] || 'date'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Scouting report downloaded!');
  };

  const renderBulletedText = (text) => {
    if (!text) return null;
    const lines = text.split('\n').filter(line => line.trim());
    const hasBullets = lines.some(line => line.trim().startsWith('•') || line.trim().startsWith('-') || line.trim().startsWith('*'));
    
    if (hasBullets) {
      return (
        <ul className="list-disc list-inside space-y-1">
          {lines.map((line, idx) => {
            const cleanLine = line.trim().replace(/^[•\-\*]\s*/, '');
            return <li key={idx} className="text-sm">{cleanLine}</li>;
          })}
        </ul>
      );
    }
    return <p className="text-sm">{text}</p>;
  };

  // Email report functionality
  const handleEmailReport = async (reportType) => {
    if (!emailReportRecipient) {
      toast.error('Please enter recipient email');
      return;
    }
    
    setIsSendingReport(true);
    try {
      let reportContent = '';
      let subject = 'PAC Report';
      
      if (reportType === 'attendance' && attendanceReport) {
        subject = `Attendance Report (${dateRange.start} to ${dateRange.end})`;
        reportContent = `
          <h2>Attendance Summary</h2>
          <p>Total Players: ${attendanceReport.total_players}</p>
          <p>Total Practices: ${attendanceReport.total_practices}</p>
          <p>Team Average: ${attendanceReport.team_attendance_percentage}%</p>
          <h3>Player Details</h3>
          <table border="1" style="border-collapse: collapse; width: 100%;">
            <tr style="background: #1d4ed8; color: white;">
              <th style="padding: 8px;">Player</th>
              <th style="padding: 8px;">Present</th>
              <th style="padding: 8px;">Absent</th>
              <th style="padding: 8px;">Attendance %</th>
            </tr>
            ${attendanceReport.player_stats?.map(p => `
              <tr>
                <td style="padding: 8px;">${p.first_name} ${p.last_name}</td>
                <td style="padding: 8px; text-align: center;">${p.present_count}</td>
                <td style="padding: 8px; text-align: center;">${p.absent_count}</td>
                <td style="padding: 8px; text-align: center;">${p.attendance_percentage}%</td>
              </tr>
            `).join('') || ''}
          </table>
        `;
      } else if (reportType === 'boxscore' && boxScoreData && selectedBoxScore) {
        subject = `Box Score - ${selectedBoxScore.game_name} (${selectedBoxScore.game_date})`;
        reportContent = `
          <h2>${selectedBoxScore.game_name}</h2>
          <p>Date: ${selectedBoxScore.game_date} ${selectedBoxScore.opponent ? `vs ${selectedBoxScore.opponent}` : ''}</p>
          <h3 style="font-size: 36px; color: #1d4ed8;">${boxScoreData.team_totals?.total_points || 0} POINTS</h3>
          <table border="1" style="border-collapse: collapse; width: 100%;">
            <tr style="background: #1d4ed8; color: white;">
              <th style="padding: 8px;">Player</th>
              <th style="padding: 8px;">PTS</th>
              <th style="padding: 8px;">REB</th>
              <th style="padding: 8px;">AST</th>
              <th style="padding: 8px;">STL</th>
              <th style="padding: 8px;">TO</th>
            </tr>
            ${boxScoreData.player_stats?.map(p => `
              <tr>
                <td style="padding: 8px;">${p.player_name}</td>
                <td style="padding: 8px; text-align: center; font-weight: bold;">${p.total_points}</td>
                <td style="padding: 8px; text-align: center;">${p.total_rebounds}</td>
                <td style="padding: 8px; text-align: center;">${p.assists}</td>
                <td style="padding: 8px; text-align: center;">${p.steals}</td>
                <td style="padding: 8px; text-align: center;">${p.turnovers}</td>
              </tr>
            `).join('') || ''}
          </table>
        `;
      }
      
      await axios.post(`${API}/contacts/email`, {
        team_id: user.team_id,
        type: 'report',
        recipient_email: emailReportRecipient,
        contacts: [{ report_content: reportContent, subject }]
      });
      
      toast.success(`Report sent to ${emailReportRecipient}!`);
      setShowEmailReportDialog(false);
      setEmailReportRecipient('');
    } catch (error) {
      toast.error('Failed to send report email');
    } finally {
      setIsSendingReport(false);
    }
  };

  // Print all reports
  const printAllReports = () => {
    const printWindow = window.open('', '_blank');
    let html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>PAC Reports Summary</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #1d4ed8; }
            h2 { color: #1d4ed8; border-bottom: 2px solid #1d4ed8; padding-bottom: 5px; margin-top: 30px; }
            table { width: 100%; border-collapse: collapse; margin: 15px 0; }
            th { background-color: #1d4ed8; color: white; padding: 10px; }
            td { border: 1px solid #ddd; padding: 8px; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .footer { margin-top: 30px; color: #666; font-size: 12px; text-align: center; }
          </style>
        </head>
        <body>
          <h1>🏀 PAC Reports Summary</h1>
          <p>Generated: ${new Date().toLocaleDateString()}</p>
    `;
    
    // Add attendance section if available
    if (attendanceReport?.player_stats?.length > 0) {
      html += `
        <h2>Attendance Report (${dateRange.start} to ${dateRange.end})</h2>
        <p>Team Average: ${attendanceReport.team_attendance_percentage}%</p>
        <table>
          <tr><th>Player</th><th>Present</th><th>Absent</th><th>%</th></tr>
          ${attendanceReport.player_stats.map(p => `
            <tr>
              <td>${p.first_name} ${p.last_name}</td>
              <td>${p.present_count}</td>
              <td>${p.absent_count}</td>
              <td>${p.attendance_percentage}%</td>
            </tr>
          `).join('')}
        </table>
      `;
    }
    
    // Add scouting reports
    if (scoutingReports.length > 0) {
      html += `<h2>Scouting Reports</h2>`;
      scoutingReports.forEach(report => {
        html += `
          <div style="margin-bottom: 20px; padding: 15px; border: 1px solid #ddd; border-radius: 8px;">
            <h3>vs ${report.opponent || report.opponent_name}</h3>
            ${report.strengths ? `<p><strong>Strengths:</strong> ${report.strengths}</p>` : ''}
            ${report.weaknesses ? `<p><strong>Weaknesses:</strong> ${report.weaknesses}</p>` : ''}
            ${report.game_plan ? `<p><strong>Game Plan:</strong> ${report.game_plan}</p>` : ''}
            ${report.content ? `<pre style="white-space: pre-wrap; font-size: 12px;">${report.content}</pre>` : ''}
          </div>
        `;
      });
    }
    
    html += `
          <div class="footer">Generated by PAC (Personal AI Coach)</div>
        </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  // Export all reports to CSV
  const exportAllReportsCSV = () => {
    let csvContent = 'ATTENDANCE REPORT\n';
    csvContent += 'Player,Present,Absent,Total,Attendance %\n';
    
    if (attendanceReport?.player_stats) {
      attendanceReport.player_stats.forEach(p => {
        csvContent += `"${p.first_name} ${p.last_name}",${p.present_count},${p.absent_count},${p.total_practices},${p.attendance_percentage}%\n`;
      });
    }
    
    csvContent += '\nSCOUTING REPORTS\n';
    csvContent += 'Opponent,Strengths,Weaknesses,Game Plan\n';
    scoutingReports.forEach(r => {
      csvContent += `"${r.opponent_name || r.opponent}","${(r.strengths || '').replace(/"/g, '""')}","${(r.weaknesses || '').replace(/"/g, '""')}","${(r.game_plan || '').replace(/"/g, '""')}"\n`;
    });
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pac_reports_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Reports exported!');
  };

  const generateAIScoutingReport = async (e) => {
    e.preventDefault();
    if (!aiScoutRequest.opponent_name) {
      toast.error('Please enter opponent name');
      return;
    }

    setIsGeneratingAI(true);
    try {
      // Kicks off a background task; response returns almost immediately
      const res = await axios.post(`${API}/ai/generate-scouting-report`, {
        team_id: user.team_id,
        opponent_name: aiScoutRequest.opponent_name,
        opponent_info: aiScoutRequest.opponent_info,
        our_strengths: aiScoutRequest.our_strengths ? aiScoutRequest.our_strengths.split(',').map(s => s.trim()) : [],
        areas_to_exploit: aiScoutRequest.areas_to_exploit ? aiScoutRequest.areas_to_exploit.split(',').map(s => s.trim()) : []
      });

      const reportId = res.data?.report_id;
      if (!reportId) throw new Error('No report_id returned');

      toast.info('AI is analyzing… this can take up to a minute.');
      setAiScoutDialog(false);
      setAiScoutRequest({ opponent_name: '', opponent_info: '', our_strengths: '', areas_to_exploit: '' });

      // Poll every 3s until ai_status leaves "generating" (or timeout at 3 min)
      const started = Date.now();
      const poll = setInterval(async () => {
        try {
          const r = await axios.get(`${API}/scouting-reports/${reportId}`);
          const status = r.data?.ai_status;
          if (status && status !== 'generating') {
            clearInterval(poll);
            setIsGeneratingAI(false);
            if (status === 'completed') {
              toast.success('AI scouting report generated!');
            } else if (status === 'completed_fallback') {
              toast.warning('AI unavailable — template scouting report saved. Top up your Universal Key for a full AI report.');
            } else {
              toast.error('AI scouting report failed — a template version was saved as a fallback.');
            }
            loadScoutingReports();
          }
        } catch (err) {
          // 404 during propagation, keep polling
        }
        if (Date.now() - started > 180000) {
          clearInterval(poll);
          setIsGeneratingAI(false);
          toast.error('AI generation timed out. The report may still finish in the background — refresh the page in a minute.');
          loadScoutingReports();
        }
      }, 3000);
    } catch (error) {
      setIsGeneratingAI(false);
      toast.error(error.response?.data?.detail || 'Failed to start AI scouting report');
    }
  };

  const exportBoxScoreCSV = () => {
    if (!boxScoreData?.player_stats) return;
    
    const headers = ['Player', 'PTS', '2PT Made', '2PT Att', '3PT Made', '3PT Att', 'FT Made', 'FT Att', 'REB', 'AST', 'STL', 'TO', 'FOULS'];
    const rows = boxScoreData.player_stats.map(p => [
      p.player_name,
      p.total_points,
      p.points_2pt,
      (p.points_2pt || 0) + (p.points_2pt_miss || 0),
      p.points_3pt,
      (p.points_3pt || 0) + (p.points_3pt_miss || 0),
      p.points_ft,
      (p.points_ft || 0) + (p.points_ft_miss || 0),
      p.total_rebounds,
      p.assists,
      p.steals,
      p.turnovers,
      p.fouls || 0
    ]);
    
    // Add totals row
    rows.push([
      'TEAM TOTAL',
      boxScoreData.team_totals?.total_points || 0,
      '', '', '', '', '', '',
      boxScoreData.team_totals?.total_rebounds || 0,
      boxScoreData.team_totals?.total_assists || 0,
      boxScoreData.team_totals?.total_steals || 0,
      boxScoreData.team_totals?.total_turnovers || 0,
      boxScoreData.team_totals?.total_fouls || 0
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `boxscore_${selectedBoxScore?.game_name?.replace(/\s/g, '_') || 'game'}_${selectedBoxScore?.game_date || 'date'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Box score exported!');
  };

  const printBoxScore = () => {
    if (!boxScoreData || !selectedBoxScore) return;
    
    const printWindow = window.open('', '_blank');
    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Box Score - ${selectedBoxScore.game_name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #1d4ed8; margin-bottom: 5px; }
            .meta { color: #666; margin-bottom: 20px; }
            .score { font-size: 48px; font-weight: bold; color: #1d4ed8; text-align: center; margin: 20px 0; }
            .quarters { display: flex; justify-content: center; gap: 20px; margin-bottom: 20px; background: #f3f4f6; padding: 10px; border-radius: 8px; }
            .quarter { text-align: center; }
            .quarter-score { font-size: 24px; font-weight: bold; }
            .quarter-label { font-size: 12px; color: #666; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background-color: #1d4ed8; color: white; padding: 10px; text-align: center; font-size: 12px; }
            td { border: 1px solid #ddd; padding: 8px; text-align: center; }
            .player-name { text-align: left !important; font-weight: 500; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .totals { font-weight: bold; background-color: #e5e7eb !important; }
            .footer { margin-top: 20px; color: #666; font-size: 12px; text-align: center; }
          </style>
        </head>
        <body>
          <h1>🏀 ${selectedBoxScore.game_name}</h1>
          <div class="meta">${selectedBoxScore.game_date} ${selectedBoxScore.opponent ? `vs ${selectedBoxScore.opponent}` : ''}</div>
          
          <div class="score">${boxScoreData.team_totals?.total_points || 0}</div>
          
          <div class="quarters">
            <div class="quarter"><div class="quarter-score">${boxScoreData.quarter_totals?.q1 || 0}</div><div class="quarter-label">Q1</div></div>
            <div class="quarter"><div class="quarter-score">${boxScoreData.quarter_totals?.q2 || 0}</div><div class="quarter-label">Q2</div></div>
            <div class="quarter"><div class="quarter-score">${boxScoreData.quarter_totals?.q3 || 0}</div><div class="quarter-label">Q3</div></div>
            <div class="quarter"><div class="quarter-score">${boxScoreData.quarter_totals?.q4 || 0}</div><div class="quarter-label">Q4</div></div>
            ${boxScoreData.quarter_totals?.ot ? `<div class="quarter"><div class="quarter-score">${boxScoreData.quarter_totals.ot}</div><div class="quarter-label">OT</div></div>` : ''}
          </div>
          
          <table>
            <thead>
              <tr>
                <th style="text-align: left;">Player</th>
                <th>PTS</th>
                <th>2PT</th>
                <th>3PT</th>
                <th>FT</th>
                <th>REB</th>
                <th>AST</th>
                <th>STL</th>
                <th>TO</th>
              </tr>
            </thead>
            <tbody>
              ${boxScoreData.player_stats?.map(p => {
                const fg2Att = (p.points_2pt || 0) + (p.points_2pt_miss || 0);
                const fg3Att = (p.points_3pt || 0) + (p.points_3pt_miss || 0);
                const ftAtt = (p.points_ft || 0) + (p.points_ft_miss || 0);
                return `
                <tr>
                  <td class="player-name">${p.player_name}</td>
                  <td><strong>${p.total_points}</strong></td>
                  <td>${p.points_2pt}-${fg2Att}</td>
                  <td>${p.points_3pt}-${fg3Att}</td>
                  <td>${p.points_ft}-${ftAtt}</td>
                  <td>${p.total_rebounds}</td>
                  <td>${p.assists}</td>
                  <td>${p.steals}</td>
                  <td>${p.turnovers}</td>
                </tr>
              `}).join('')}
              <tr class="totals">
                <td class="player-name">TEAM TOTALS</td>
                <td><strong>${boxScoreData.team_totals?.total_points || 0}</strong></td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>${boxScoreData.team_totals?.total_rebounds || 0}</td>
                <td>${boxScoreData.team_totals?.total_assists || 0}</td>
                <td>${boxScoreData.team_totals?.total_steals || 0}</td>
                <td>${boxScoreData.team_totals?.total_turnovers || 0}</td>
              </tr>
            </tbody>
          </table>
          <div class="footer">Generated by PAC (Personal AI Coach) • ${new Date().toLocaleDateString()}</div>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8 space-y-8" data-testid="reports-page">
        {!canView('reports') ? (
          <NoAccessScreen area="Reports" />
        ) : (
        <>
        {isReadOnly('reports') && <ReadOnlyBanner area="Reports" />}
        <div className="flex items-center justify-between border-b border-border pb-6">
          <div>
            <h1 className="font-heading text-5xl font-black uppercase tracking-tighter text-primary">
              Reports & Analytics
            </h1>
            <p className="text-muted-foreground mt-2">Generate and export performance and attendance reports</p>
          </div>
          <div className="flex gap-2">
            {/* Export All Reports */}
            <Button onClick={exportAllReportsCSV} data-testid="export-all-reports-btn">
              <Download className="h-4 w-4 mr-2" />
              EXPORT ALL
            </Button>
            
            {/* Print All Reports */}
            <Button onClick={printAllReports} data-testid="print-all-reports-btn">
              <Printer className="h-4 w-4 mr-2" />
              PRINT
            </Button>
            
            {/* Email Reports Dialog */}
            <Dialog open={showEmailReportDialog} onOpenChange={setShowEmailReportDialog}>
              <DialogTrigger asChild>
                <Button data-testid="email-reports-btn">
                  <Mail className="h-4 w-4 mr-2" />
                  EMAIL
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl uppercase">Email Report</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Recipient Email</Label>
                    <Input
                      type="email"
                      value={emailReportRecipient}
                      onChange={(e) => setEmailReportRecipient(e.target.value)}
                      placeholder="coach@team.com"
                      data-testid="email-report-recipient"
                    />
                  </div>
                  <div>
                    <Label>Select Report to Send</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <Button 
                        variant="outline"
                        onClick={() => handleEmailReport('attendance')}
                        disabled={isSendingReport || !attendanceReport?.player_stats}
                      >
                        <UserCheck className="h-4 w-4 mr-2" />
                        Attendance
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => handleEmailReport('boxscore')}
                        disabled={isSendingReport || !boxScoreData}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Box Score
                      </Button>
                    </div>
                    {isSendingReport && (
                      <p className="text-sm text-muted-foreground text-center mt-2">
                        <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                        Sending...
                      </p>
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            
            {/* Upload Report */}
            <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
              <DialogTrigger asChild>
                <Button data-testid="upload-report-btn">
                  <Upload className="h-4 w-4 mr-2" />
                  UPLOAD
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl uppercase">Upload Report</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div {...getRootProps()} className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    isDragActive ? 'border-primary bg-primary/10' : 'border-muted-foreground/20 hover:border-primary/50'
                  }`}>
                    <input {...getInputProps()} />
                    <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    {uploadFile ? (
                      <p className="text-sm font-semibold">{uploadFile.name}</p>
                    ) : (
                      <div>
                        <p className="text-sm font-semibold">Drop report file here or click to browse</p>
                        <p className="text-xs text-muted-foreground mt-1">Supports PDF, Excel, CSV</p>
                      </div>
                    )}
                  </div>
                  <Button onClick={handleUploadReport} className="w-full" data-testid="submit-upload-btn">
                    <Upload className="h-4 w-4 mr-2" />
                    UPLOAD REPORT
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Tabs defaultValue="attendance" className="space-y-6">
          <TabsList className="bg-muted">
            <TabsTrigger value="attendance" data-testid="attendance-tab">
              <UserCheck className="h-4 w-4 mr-2" />
              Attendance
            </TabsTrigger>
            <TabsTrigger value="boxscores" data-testid="boxscores-tab">
              <FileText className="h-4 w-4 mr-2" />
              Box Scores
            </TabsTrigger>
            <TabsTrigger value="scouting" data-testid="scouting-tab">
              <TrendingUp className="h-4 w-4 mr-2" />
              Scouting Reports
            </TabsTrigger>
            <TabsTrigger value="performance" data-testid="performance-tab">
              <Users className="h-4 w-4 mr-2" />
              Player Stats
            </TabsTrigger>
            <TabsTrigger value="compare" data-testid="compare-tab">
              <TrendingUp className="h-4 w-4 mr-2" />
              Compare Players
            </TabsTrigger>
            <TabsTrigger value="trends" data-testid="trends-tab">
              <BarChart3 className="h-4 w-4 mr-2" />
              Season Trends
            </TabsTrigger>
          </TabsList>

          {/* Attendance Reports Tab */}
          <TabsContent value="attendance" className="space-y-6">
            {/* Date Range Filter */}
            <Card className="border-2">
              <CardHeader className="border-b bg-muted/30">
                <CardTitle className="font-heading uppercase text-xl flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Attendance Report Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="flex flex-wrap items-end gap-4">
                  <div>
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={dateRange.start}
                      onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                      className="w-40"
                      data-testid="attendance-start-date"
                    />
                  </div>
                  <div>
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={dateRange.end}
                      onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                      className="w-40"
                      data-testid="attendance-end-date"
                    />
                  </div>
                  <Button onClick={loadAttendanceReport} disabled={isLoadingAttendance} data-testid="generate-attendance-report-btn">
                    {isLoadingAttendance ? 'Loading...' : 'GENERATE REPORT'}
                  </Button>
                  <Button variant="outline" onClick={exportAttendanceCSV} disabled={!attendanceReport?.player_stats} data-testid="export-attendance-csv-btn">
                    <Download className="h-4 w-4 mr-2" />
                    EXPORT CSV
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={sendAttendanceAlerts} 
                    disabled={isSendingAlerts || !attendanceReport?.players_below_75}
                    data-testid="send-alerts-btn"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    {isSendingAlerts ? 'SENDING...' : `ALERT PARENTS (${attendanceReport?.players_below_75 || 0})`}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Summary Stats */}
            {attendanceReport && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="border-2 bg-blue-50">
                  <CardContent className="p-6 text-center">
                    <Users className="h-8 w-8 mx-auto text-blue-600 mb-2" />
                    <p className="text-3xl font-bold text-blue-700">{attendanceReport.total_players}</p>
                    <p className="text-sm text-muted-foreground uppercase">Total Players</p>
                  </CardContent>
                </Card>
                <Card className="border-2 bg-green-50">
                  <CardContent className="p-6 text-center">
                    <Calendar className="h-8 w-8 mx-auto text-green-600 mb-2" />
                    <p className="text-3xl font-bold text-green-700">{attendanceReport.total_practices}</p>
                    <p className="text-sm text-muted-foreground uppercase">Total Practices</p>
                  </CardContent>
                </Card>
                <Card className="border-2 bg-purple-50">
                  <CardContent className="p-6 text-center">
                    <TrendingUp className="h-8 w-8 mx-auto text-purple-600 mb-2" />
                    <p className="text-3xl font-bold text-purple-700">{attendanceReport.team_attendance_percentage}%</p>
                    <p className="text-sm text-muted-foreground uppercase">Team Avg Attendance</p>
                  </CardContent>
                </Card>
                <Card className="border-2 bg-orange-50">
                  <CardContent className="p-6 text-center">
                    <AlertTriangle className="h-8 w-8 mx-auto text-orange-600 mb-2" />
                    <p className="text-3xl font-bold text-orange-700">{attendanceReport.players_below_75 || 0}</p>
                    <p className="text-sm text-muted-foreground uppercase">Below 75% Attendance</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Player Attendance Table */}
            <Card className="border-2">
              <CardHeader className="border-b bg-muted/30">
                <CardTitle className="font-heading uppercase text-xl flex items-center gap-2">
                  <UserCheck className="h-5 w-5" />
                  Player Attendance Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {!attendanceReport?.player_stats || attendanceReport.player_stats.length === 0 ? (
                  <div className="text-center py-8">
                    <UserCheck className="h-12 w-12 mx-auto text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground mt-4">No attendance data available for the selected period.</p>
                    <p className="text-sm text-muted-foreground">Take attendance on the Practice page to see reports here.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="attendance-table">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left p-3 font-heading uppercase text-sm">Player</th>
                          <th className="text-center p-3 font-heading uppercase text-sm">Jersey</th>
                          <th className="text-center p-3 font-heading uppercase text-sm">Present</th>
                          <th className="text-center p-3 font-heading uppercase text-sm">Absent</th>
                          <th className="text-center p-3 font-heading uppercase text-sm">Total</th>
                          <th className="text-center p-3 font-heading uppercase text-sm">Attendance %</th>
                          <th className="text-left p-3 font-heading uppercase text-sm">Trend</th>
                        </tr>
                      </thead>
                      <tbody>
                        {attendanceReport.player_stats
                          .sort((a, b) => b.attendance_percentage - a.attendance_percentage)
                          .map((player, index) => (
                          <tr key={player.player_id} className="border-b hover:bg-muted/30" data-testid={`attendance-row-${player.player_id}`}>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold">{player.first_name} {player.last_name}</span>
                                {player.attendance_percentage < 75 && (
                                  <Badge variant="destructive" className="text-xs">Low</Badge>
                                )}
                              </div>
                            </td>
                            <td className="text-center p-3">
                              <Badge variant="outline">#{player.jersey_number || '?'}</Badge>
                            </td>
                            <td className="text-center p-3">
                              <span className="text-green-600 font-bold">{player.present_count}</span>
                            </td>
                            <td className="text-center p-3">
                              <span className="text-red-600 font-bold">{player.absent_count}</span>
                            </td>
                            <td className="text-center p-3">{player.total_practices}</td>
                            <td className="text-center p-3">
                              <span className={`px-3 py-1 rounded-full font-bold ${getAttendanceColor(player.attendance_percentage)}`}>
                                {player.attendance_percentage}%
                              </span>
                            </td>
                            <td className="p-3 w-40">
                              <Progress 
                                value={player.attendance_percentage} 
                                className="h-2"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Attendance History */}
            {attendanceHistory.length > 0 && (
              <Card className="border-2">
                <CardHeader className="border-b bg-muted/30">
                  <CardTitle className="font-heading uppercase text-xl flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Recent Attendance History
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {attendanceHistory.slice(0, 12).map((day) => (
                      <div key={day.date} className="p-3 rounded-lg border bg-muted/30 text-center">
                        <p className="text-xs text-muted-foreground">{new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}</p>
                        <p className="font-semibold">{new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                        <p className={`text-lg font-bold mt-1 ${day.present_count / day.total_count >= 0.75 ? 'text-green-600' : 'text-orange-600'}`}>
                          {day.present_count}/{day.total_count}
                        </p>
                        <p className="text-xs text-muted-foreground">present</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Performance Reports Tab */}
          <TabsContent value="performance" className="space-y-6">
            <Card className="border-2">
              <CardHeader className="border-b bg-muted/30">
                <CardTitle className="font-heading uppercase text-2xl">Player Performance Report</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="flex gap-4">
                  <Select value={selectedPlayer} onValueChange={setSelectedPlayer}>
                    <SelectTrigger className="flex-1" data-testid="select-player-report">
                      <SelectValue placeholder="Select a player" />
                    </SelectTrigger>
                    <SelectContent>
                      {players.map((player) => (
                        <SelectItem key={player.id} value={player.id}>
                          #{player.jersey_number} - {player.first_name} {player.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={generateReport} data-testid="generate-report-btn">
                    GENERATE REPORT
                  </Button>
                </div>

                {reportData && (
                  <div className="mt-6 p-6 bg-muted/50 rounded-lg">
                    <h3 className="font-heading text-xl uppercase mb-4">
                      {reportData.player?.first_name} {reportData.player?.last_name} - #{reportData.player?.jersey_number}
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase font-semibold">Total Points</p>
                        <p className="text-2xl font-bold">{reportData.stats?.total_points || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase font-semibold">FG %</p>
                        <p className="text-2xl font-bold">{reportData.stats?.field_goal_percentage || 0}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase font-semibold">3PT %</p>
                        <p className="text-2xl font-bold">{reportData.stats?.three_point_percentage || 0}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase font-semibold">FT %</p>
                        <p className="text-2xl font-bold">{reportData.stats?.free_throw_percentage || 0}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase font-semibold">Rebounds</p>
                        <p className="text-2xl font-bold">{reportData.stats?.total_rebounds || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase font-semibold">Assists</p>
                        <p className="text-2xl font-bold">{reportData.stats?.total_assists || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase font-semibold">Steals</p>
                        <p className="text-2xl font-bold">{reportData.stats?.total_steals || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase font-semibold">Turnovers</p>
                        <p className="text-2xl font-bold">{reportData.stats?.total_turnovers || 0}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-6">
                      <Button variant="outline" data-testid="export-pdf-btn">
                        <Download className="h-4 w-4 mr-2" />
                        EXPORT PDF
                      </Button>
                      <Button variant="outline" data-testid="export-csv-btn">
                        <Download className="h-4 w-4 mr-2" />
                        EXPORT CSV
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Box Scores Tab */}
          <TabsContent value="boxscores" className="space-y-6">
            <Card className="border-2">
              <CardHeader className="border-b bg-muted/30 flex flex-row items-center justify-between">
                <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                  <FileText className="h-6 w-6" />
                  Game Box Scores
                </CardTitle>
                {selectedBoxScore && boxScoreData && (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => { setRecapText(null); generateRecap(false); }} data-testid="ai-recap-btn">
                      <Sparkles className="h-4 w-4 mr-2" />
                      AI RECAP
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportBoxScoreCSV} data-testid="export-boxscore-csv">
                      <Download className="h-4 w-4 mr-2" />
                      CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={printBoxScore} data-testid="print-boxscore">
                      <Printer className="h-4 w-4 mr-2" />
                      PRINT
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="p-6">
                {gameSessions.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No completed games yet. Box scores will appear after games are tracked on the Stats page.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {gameSessions.map((session) => (
                      <Card key={session.id} className={`border-2 cursor-pointer transition-colors ${selectedBoxScore?.id === session.id ? 'border-primary' : 'hover:border-primary/50'}`}
                        onClick={() => loadBoxScore(session)}
                        data-testid={`boxscore-card-${session.id}`}
                      >
                        <CardContent className="p-4">
                          <h4 className="font-heading text-lg font-bold uppercase">{session.game_name}</h4>
                          <p className="text-sm text-muted-foreground">{session.game_date}</p>
                          {session.opponent && <p className="text-sm">vs {session.opponent}</p>}
                          <Badge className="mt-2 bg-green-500">Completed</Badge>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* AI Game Recap Dialog */}
            <Dialog open={showRecapDialog} onOpenChange={setShowRecapDialog}>
              <DialogContent className="max-w-2xl" data-testid="recap-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl uppercase flex items-center gap-2">
                    <Sparkles className="h-6 w-6 text-primary" />
                    AI Game Recap
                  </DialogTitle>
                </DialogHeader>
                {isGeneratingRecap ? (
                  <div className="text-center py-12">
                    <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary mb-4" />
                    <p className="font-semibold">Claude is writing your recap...</p>
                  </div>
                ) : recapText ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-muted/40 rounded-lg border max-h-80 overflow-y-auto">
                      <p className="text-sm whitespace-pre-wrap" data-testid="recap-text">{recapText}</p>
                    </div>
                    <div className="flex justify-end gap-2 flex-wrap">
                      <VoicePlayer text={recapText} userId={user.id} source="recap" label="Listen" variant="outline" size="default" />
                      <Button variant="outline" onClick={() => generateRecap(true)} data-testid="regenerate-recap-btn">
                        <Sparkles className="h-4 w-4 mr-2" />
                        Regenerate
                      </Button>
                      <Button variant="outline" onClick={postRecapToSocial} data-testid="post-recap-to-social-btn">
                        <Share2 className="h-4 w-4 mr-2" />
                        Post to Social
                      </Button>
                      <Button onClick={copyRecap} data-testid="copy-recap-btn">
                        <Download className="h-4 w-4 mr-2" />
                        Copy to Share
                      </Button>
                    </div>
                  </div>
                ) : null}
              </DialogContent>
            </Dialog>

            {/* Selected Box Score Display */}
            {boxScoreData && selectedBoxScore && (
              <Card className="border-2 border-primary">
                <CardHeader className="border-b bg-primary/5">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="font-heading text-2xl uppercase">{selectedBoxScore.game_name}</CardTitle>
                      <p className="text-muted-foreground">{selectedBoxScore.game_date} {selectedBoxScore.opponent && `vs ${selectedBoxScore.opponent}`}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-4xl font-bold text-primary">{boxScoreData.team_totals?.total_points || 0}</p>
                      <p className="text-sm text-muted-foreground">TEAM POINTS</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  {/* Quarter Breakdown */}
                  {boxScoreData.quarter_totals && (
                    <div className="flex justify-center gap-4 py-3 bg-muted/50 rounded-lg mb-4">
                      <div className="text-center px-4">
                        <p className="text-lg font-bold">{boxScoreData.quarter_totals?.q1 || 0}</p>
                        <p className="text-xs text-muted-foreground">Q1</p>
                      </div>
                      <div className="text-center px-4 border-l">
                        <p className="text-lg font-bold">{boxScoreData.quarter_totals?.q2 || 0}</p>
                        <p className="text-xs text-muted-foreground">Q2</p>
                      </div>
                      <div className="text-center px-4 border-l">
                        <p className="text-lg font-bold">{boxScoreData.quarter_totals?.q3 || 0}</p>
                        <p className="text-xs text-muted-foreground">Q3</p>
                      </div>
                      <div className="text-center px-4 border-l">
                        <p className="text-lg font-bold">{boxScoreData.quarter_totals?.q4 || 0}</p>
                        <p className="text-xs text-muted-foreground">Q4</p>
                      </div>
                      {boxScoreData.quarter_totals?.ot > 0 && (
                        <div className="text-center px-4 border-l">
                          <p className="text-lg font-bold">{boxScoreData.quarter_totals?.ot || 0}</p>
                          <p className="text-xs text-muted-foreground">OT</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* View Toggle */}
                  <div className="flex justify-end mb-4">
                    <div className="inline-flex rounded-lg border p-1 bg-muted/50">
                      <Button
                        variant={boxScoreViewMode === 'totals' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setBoxScoreViewMode('totals')}
                        data-testid="boxscore-view-totals"
                      >
                        Game Totals
                      </Button>
                      <Button
                        variant={boxScoreViewMode === 'quarters' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setBoxScoreViewMode('quarters')}
                        data-testid="boxscore-view-quarters"
                      >
                        By Quarter
                      </Button>
                    </div>
                  </div>

                  {/* Stats Table - Totals View */}
                  {boxScoreViewMode === 'totals' && (
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse text-sm" data-testid="boxscore-totals-table">
                        <thead>
                          <tr className="bg-primary text-primary-foreground">
                            <th className="border p-2 text-left">Player</th>
                            <th className="border p-2 text-center">PTS</th>
                            <th className="border p-2 text-center">2PT</th>
                            <th className="border p-2 text-center">3PT</th>
                            <th className="border p-2 text-center">FT</th>
                            <th className="border p-2 text-center">REB</th>
                            <th className="border p-2 text-center">AST</th>
                            <th className="border p-2 text-center">STL</th>
                            <th className="border p-2 text-center">TO</th>
                          </tr>
                        </thead>
                        <tbody>
                          {boxScoreData.player_stats?.map((player, index) => {
                            const fg2Att = (player.points_2pt || 0) + (player.points_2pt_miss || 0);
                            const fg3Att = (player.points_3pt || 0) + (player.points_3pt_miss || 0);
                            const ftAtt = (player.points_ft || 0) + (player.points_ft_miss || 0);
                            return (
                              <tr key={player.player_id} className={index % 2 === 0 ? 'bg-muted/30' : ''}>
                                <td className="border p-2 font-medium">{player.player_name}</td>
                                <td className="border p-2 text-center font-bold">{player.total_points}</td>
                                <td className="border p-2 text-center">{player.points_2pt}-{fg2Att}</td>
                                <td className="border p-2 text-center">{player.points_3pt}-{fg3Att}</td>
                                <td className="border p-2 text-center">{player.points_ft}-{ftAtt}</td>
                                <td className="border p-2 text-center">{player.total_rebounds}</td>
                                <td className="border p-2 text-center">{player.assists}</td>
                                <td className="border p-2 text-center">{player.steals}</td>
                                <td className="border p-2 text-center">{player.turnovers}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Stats Table - By Quarter View */}
                  {boxScoreViewMode === 'quarters' && (
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse text-sm" data-testid="boxscore-quarters-table">
                        <thead>
                          <tr className="bg-primary text-primary-foreground">
                            <th className="border p-2 text-left">Player</th>
                            <th className="border p-2 text-center">Q1</th>
                            <th className="border p-2 text-center">Q2</th>
                            <th className="border p-2 text-center">Q3</th>
                            <th className="border p-2 text-center">Q4</th>
                            {boxScoreData.quarter_totals?.ot > 0 && (
                              <th className="border p-2 text-center">OT</th>
                            )}
                            <th className="border p-2 text-center bg-primary/80">TOTAL</th>
                          </tr>
                        </thead>
                        <tbody>
                          {boxScoreData.player_stats?.map((player, index) => (
                            <tr key={player.player_id} className={index % 2 === 0 ? 'bg-muted/30' : ''}>
                              <td className="border p-2 font-medium">{player.player_name}</td>
                              <td className="border p-2 text-center">{player.quarter_points?.q1 || 0}</td>
                              <td className="border p-2 text-center">{player.quarter_points?.q2 || 0}</td>
                              <td className="border p-2 text-center">{player.quarter_points?.q3 || 0}</td>
                              <td className="border p-2 text-center">{player.quarter_points?.q4 || 0}</td>
                              {boxScoreData.quarter_totals?.ot > 0 && (
                                <td className="border p-2 text-center">{player.quarter_points?.ot || 0}</td>
                              )}
                              <td className="border p-2 text-center font-bold bg-muted">{player.total_points}</td>
                            </tr>
                          ))}
                          <tr className="bg-muted font-bold">
                            <td className="border p-2">TEAM</td>
                            <td className="border p-2 text-center">{boxScoreData.quarter_totals?.q1 || 0}</td>
                            <td className="border p-2 text-center">{boxScoreData.quarter_totals?.q2 || 0}</td>
                            <td className="border p-2 text-center">{boxScoreData.quarter_totals?.q3 || 0}</td>
                            <td className="border p-2 text-center">{boxScoreData.quarter_totals?.q4 || 0}</td>
                            {boxScoreData.quarter_totals?.ot > 0 && (
                              <td className="border p-2 text-center">{boxScoreData.quarter_totals?.ot || 0}</td>
                            )}
                            <td className="border p-2 text-center">{boxScoreData.team_totals?.total_points || 0}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Scouting Reports Tab */}
          <TabsContent value="scouting" className="space-y-6">
            <Card className="border-2">
              <CardHeader className="border-b bg-muted/30 flex flex-row items-center justify-between">
                <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                  <TrendingUp className="h-6 w-6" />
                  Scouting Reports
                </CardTitle>
                <div className="flex gap-2">
                  {/* AI Generate Scouting Report */}
                  <Dialog open={aiScoutDialog} onOpenChange={setAiScoutDialog}>
                    <DialogTrigger asChild>
                      <Button variant="secondary" data-testid="ai-scout-report-btn">
                        <Sparkles className="h-4 w-4 mr-2" />
                        AI GENERATE
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle className="font-heading text-2xl uppercase flex items-center gap-2">
                          <Sparkles className="h-5 w-5 text-yellow-500" />
                          AI Scouting Report
                        </DialogTitle>
                      </DialogHeader>
                      <form onSubmit={generateAIScoutingReport} className="space-y-4">
                        <div>
                          <Label>Opponent Name *</Label>
                          <Input
                            value={aiScoutRequest.opponent_name}
                            onChange={(e) => setAiScoutRequest({ ...aiScoutRequest, opponent_name: e.target.value })}
                            required
                            placeholder="e.g., Eagles, Lions"
                            data-testid="ai-opponent-name"
                          />
                        </div>
                        <div>
                          <Label>Opponent Info (optional)</Label>
                          <Textarea
                            value={aiScoutRequest.opponent_info}
                            onChange={(e) => setAiScoutRequest({ ...aiScoutRequest, opponent_info: e.target.value })}
                            rows={3}
                            placeholder="Any known info: record, key players, play style..."
                            data-testid="ai-opponent-info"
                          />
                        </div>
                        <div>
                          <Label>Our Team Strengths (comma separated)</Label>
                          <Input
                            value={aiScoutRequest.our_strengths}
                            onChange={(e) => setAiScoutRequest({ ...aiScoutRequest, our_strengths: e.target.value })}
                            placeholder="e.g., fast break, 3-point shooting, defense"
                            data-testid="ai-our-strengths"
                          />
                        </div>
                        <div>
                          <Label>Areas to Exploit (comma separated)</Label>
                          <Input
                            value={aiScoutRequest.areas_to_exploit}
                            onChange={(e) => setAiScoutRequest({ ...aiScoutRequest, areas_to_exploit: e.target.value })}
                            placeholder="e.g., weak perimeter defense, turnovers"
                            data-testid="ai-areas-exploit"
                          />
                        </div>
                        <Button type="submit" className="w-full" disabled={isGeneratingAI} data-testid="generate-ai-scout-btn">
                          {isGeneratingAI ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              GENERATING...
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-4 w-4 mr-2" />
                              GENERATE AI REPORT
                            </>
                          )}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={showScoutDialog} onOpenChange={setShowScoutDialog}>
                    <DialogTrigger asChild>
                      <Button data-testid="add-scout-report-btn">
                        <FileText className="h-4 w-4 mr-2" />
                        MANUAL REPORT
                      </Button>
                    </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-heading text-2xl uppercase">Create Scouting Report</DialogTitle>
                      <p className="text-xs text-muted-foreground">Use • or - at the start of each line for bullet points</p>
                    </DialogHeader>
                    <form onSubmit={handleAddScoutReport} className="space-y-4">
                      <div>
                        <Label>Opponent *</Label>
                        <Input
                          value={newScout.opponent_name}
                          onChange={(e) => setNewScout({ ...newScout, opponent_name: e.target.value })}
                          required
                          placeholder="Team name"
                          data-testid="manual-scout-opponent"
                        />
                      </div>
                      <div>
                        <Label>Strengths</Label>
                        <Textarea
                          value={newScout.strengths}
                          onChange={(e) => setNewScout({ ...newScout, strengths: e.target.value })}
                          rows={3}
                          placeholder="• Strong rebounding&#10;• Fast break offense&#10;• Experienced point guard"
                          data-testid="manual-scout-strengths"
                        />
                      </div>
                      <div>
                        <Label>Weaknesses</Label>
                        <Textarea
                          value={newScout.weaknesses}
                          onChange={(e) => setNewScout({ ...newScout, weaknesses: e.target.value })}
                          rows={3}
                          placeholder="• Weak perimeter defense&#10;• Prone to turnovers&#10;• Poor free throw shooting"
                          data-testid="manual-scout-weaknesses"
                        />
                      </div>
                      <div>
                        <Label>Game Plan</Label>
                        <Textarea
                          value={newScout.game_plan}
                          onChange={(e) => setNewScout({ ...newScout, game_plan: e.target.value })}
                          rows={4}
                          placeholder="• Push tempo on offense&#10;• Press full court&#10;• Attack the paint"
                          data-testid="manual-scout-gameplan"
                        />
                      </div>
                      <Button type="submit" className="w-full" data-testid="submit-manual-scout-btn">CREATE REPORT</Button>
                    </form>
                  </DialogContent>
                </Dialog>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {scoutingReports.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No scouting reports yet. Create your first scouting report for an upcoming opponent!</p>
                ) : (
                  <div className="space-y-4">
                    {scoutingReports.map((report) => (
                      <Card key={report.id} className="border-2 hover:border-primary/50" data-testid={`scout-report-${report.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-heading text-xl font-bold uppercase">vs {report.opponent || report.opponent_name}</h4>
                                {report.ai_generated && (
                                  <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                                    <Sparkles className="h-3 w-3 mr-1" />
                                    AI
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground mb-3">
                                Created {new Date(report.created_at).toLocaleDateString()}
                              </p>
                              {report.ai_generated && report.structured ? (
                                <div className="prose prose-sm max-w-none bg-muted/30 p-3 rounded-lg max-h-64 overflow-auto">
                                  <PacReplyView structured={report.structured} />
                                </div>
                              ) : report.ai_generated && report.content ? (
                                <div className="prose prose-sm max-w-none">
                                  <pre className="whitespace-pre-wrap text-sm bg-muted/30 p-3 rounded-lg overflow-auto max-h-64">
                                    {report.content}
                                  </pre>
                                </div>
                              ) : (
                                <>
                                  {report.strengths && (
                                    <div className="mb-3">
                                      <span className="text-xs font-semibold text-green-600 block mb-1">STRENGTHS:</span>
                                      {renderBulletedText(report.strengths)}
                                    </div>
                                  )}
                                  {report.weaknesses && (
                                    <div className="mb-3">
                                      <span className="text-xs font-semibold text-red-600 block mb-1">WEAKNESSES:</span>
                                      {renderBulletedText(report.weaknesses)}
                                    </div>
                                  )}
                                  {report.game_plan && (
                                    <div className="mb-3">
                                      <span className="text-xs font-semibold text-blue-600 block mb-1">GAME PLAN:</span>
                                      {renderBulletedText(report.game_plan)}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadScoutingReport(report)}
                                data-testid={`scout-report-download-btn-${report.id}`}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                              <VoicePlayer
                                text={report.content || [report.strengths && `Strengths: ${report.strengths}`, report.weaknesses && `Weaknesses: ${report.weaknesses}`, report.game_plan && `Game plan: ${report.game_plan}`].filter(Boolean).join('. ')}
                                userId={user.id}
                                source="scouting"
                              />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Compare Players Tab */}
          <TabsContent value="compare" className="space-y-6">
            <Card className="border-2">
              <CardHeader className="border-b bg-muted/30">
                <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                  <TrendingUp className="h-6 w-6" />
                  Compare Players
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 gap-8 mb-6">
                  {/* Player 1 Selection */}
                  <div>
                    <Label className="text-lg font-semibold mb-2 block">Player 1</Label>
                    <Select value={comparePlayer1 || ''} onValueChange={handleComparePlayer1Change}>
                      <SelectTrigger data-testid="compare-player1-select">
                        <SelectValue placeholder="Select player..." />
                      </SelectTrigger>
                      <SelectContent>
                        {players.map((player) => (
                          <SelectItem key={player.id} value={player.id} disabled={player.id === comparePlayer2}>
                            #{player.jersey_number || '?'} {player.first_name} {player.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Player 2 Selection */}
                  <div>
                    <Label className="text-lg font-semibold mb-2 block">Player 2</Label>
                    <Select value={comparePlayer2 || ''} onValueChange={handleComparePlayer2Change}>
                      <SelectTrigger data-testid="compare-player2-select">
                        <SelectValue placeholder="Select player..." />
                      </SelectTrigger>
                      <SelectContent>
                        {players.map((player) => (
                          <SelectItem key={player.id} value={player.id} disabled={player.id === comparePlayer1}>
                            #{player.jersey_number || '?'} {player.first_name} {player.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Comparison Display */}
                {isLoadingComparison && (
                  <div className="text-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                    <p className="text-muted-foreground mt-2">Loading stats...</p>
                  </div>
                )}

                {!isLoadingComparison && player1Stats && player2Stats && (
                  <div className="space-y-4">
                    {/* Player Headers */}
                    <div className="grid grid-cols-3 gap-4 text-center border-b pb-4">
                      <div className="text-left">
                        <h3 className="text-2xl font-bold text-primary">
                          {player1Stats.player?.first_name} {player1Stats.player?.last_name}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          #{player1Stats.player?.jersey_number || '?'} • {player1Stats.player?.position || 'N/A'}
                        </p>
                      </div>
                      <div className="flex items-center justify-center">
                        <span className="text-xl font-black text-muted-foreground">VS</span>
                      </div>
                      <div className="text-right">
                        <h3 className="text-2xl font-bold text-primary">
                          {player2Stats.player?.first_name} {player2Stats.player?.last_name}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          #{player2Stats.player?.jersey_number || '?'} • {player2Stats.player?.position || 'N/A'}
                        </p>
                      </div>
                    </div>

                    {/* Stats Comparison Table */}
                    <div className="space-y-2">
                      {[
                        { label: 'Games Played', key: 'games_played', higher: true },
                        { label: 'Total Points', key: 'total_points', higher: true },
                        { label: 'Field Goal %', key: 'field_goal_percentage', higher: true, suffix: '%' },
                        { label: '3-Point %', key: 'three_point_percentage', higher: true, suffix: '%' },
                        { label: 'Free Throw %', key: 'free_throw_percentage', higher: true, suffix: '%' },
                        { label: 'Total Rebounds', key: 'total_rebounds', higher: true },
                        { label: 'Total Assists', key: 'total_assists', higher: true },
                        { label: 'Total Steals', key: 'total_steals', higher: true },
                        { label: 'Total Turnovers', key: 'total_turnovers', higher: false },
                        { label: 'Total Fouls', key: 'total_fouls', higher: false },
                      ].map(({ label, key, higher, suffix = '' }) => {
                        const val1 = player1Stats.stats?.[key] || 0;
                        const val2 = player2Stats.stats?.[key] || 0;
                        return (
                          <div key={key} className="grid grid-cols-3 gap-4 py-3 border-b hover:bg-muted/30 transition-colors">
                            <div className={`text-2xl font-bold text-right ${getComparisonColor(val1, val2, higher)}`}>
                              {val1}{suffix}
                            </div>
                            <div className="text-center text-sm text-muted-foreground font-semibold uppercase">
                              {label}
                            </div>
                            <div className={`text-2xl font-bold text-left ${getComparisonColor(val2, val1, higher)}`}>
                              {val2}{suffix}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Legend */}
                    <div className="flex justify-center gap-6 pt-4 text-sm text-muted-foreground">
                      <span><span className="inline-block w-3 h-3 bg-green-500 rounded mr-1"></span> Better</span>
                      <span><span className="inline-block w-3 h-3 bg-red-500 rounded mr-1"></span> Lower</span>
                      <span><span className="inline-block w-3 h-3 bg-muted rounded mr-1"></span> Equal</span>
                    </div>
                  </div>
                )}

                {!isLoadingComparison && (!player1Stats || !player2Stats) && comparePlayer1 && comparePlayer2 && (
                  <div className="text-center py-8">
                    <AlertTriangle className="h-12 w-12 mx-auto text-yellow-500 mb-2" />
                    <p className="text-muted-foreground">Unable to load stats for comparison.</p>
                  </div>
                )}

                {(!comparePlayer1 || !comparePlayer2) && (
                  <div className="text-center py-12 border-2 border-dashed rounded-lg">
                    <Users className="h-16 w-16 mx-auto text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground mt-4 text-lg">Select two players to compare their stats</p>
                    <p className="text-sm text-muted-foreground">See head-to-head performance comparison</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Season Trends Tab */}
          <TabsContent value="trends" className="space-y-6">
            {isLoadingTrends ? (
              <div className="text-center py-12">
                <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
                <p className="text-muted-foreground mt-4">Loading season trends...</p>
              </div>
            ) : (
              <>
                {/* Season Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="border-2">
                    <CardContent className="p-4 text-center">
                      <Trophy className="h-8 w-8 mx-auto text-yellow-500 mb-2" />
                      <p className="text-3xl font-bold text-primary">
                        {seasonTrends?.season_summary?.wins || 0}-{seasonTrends?.season_summary?.losses || 0}
                      </p>
                      <p className="text-sm text-muted-foreground">Win-Loss Record</p>
                      <Badge variant={seasonTrends?.season_summary?.win_percentage >= 50 ? 'default' : 'secondary'} className="mt-2">
                        {seasonTrends?.season_summary?.win_percentage || 0}% Win Rate
                      </Badge>
                    </CardContent>
                  </Card>
                  <Card className="border-2">
                    <CardContent className="p-4 text-center">
                      <Target className="h-8 w-8 mx-auto text-blue-500 mb-2" />
                      <p className="text-3xl font-bold text-primary">
                        {seasonTrends?.season_summary?.avg_points_per_game || 0}
                      </p>
                      <p className="text-sm text-muted-foreground">Avg Points/Game</p>
                    </CardContent>
                  </Card>
                  <Card className="border-2">
                    <CardContent className="p-4 text-center">
                      <TrendingUp className="h-8 w-8 mx-auto text-green-500 mb-2" />
                      <p className="text-3xl font-bold text-primary">
                        {seasonTrends?.season_summary?.avg_rebounds_per_game || 0}
                      </p>
                      <p className="text-sm text-muted-foreground">Avg Rebounds/Game</p>
                    </CardContent>
                  </Card>
                  <Card className="border-2">
                    <CardContent className="p-4 text-center">
                      <Users className="h-8 w-8 mx-auto text-purple-500 mb-2" />
                      <p className="text-3xl font-bold text-primary">
                        {seasonTrends?.season_summary?.avg_assists_per_game || 0}
                      </p>
                      <p className="text-sm text-muted-foreground">Avg Assists/Game</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Points Per Game Trend */}
                <Card className="border-2">
                  <CardHeader className="border-b bg-muted/30">
                    <CardTitle className="font-heading uppercase text-xl flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Points Per Game Trend
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    {seasonTrends?.games?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={seasonTrends.games}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="game_name" 
                            tick={{ fontSize: 12 }}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                          />
                          <YAxis />
                          <Tooltip 
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <div className="bg-background border rounded-lg p-3 shadow-lg">
                                    <p className="font-bold">{data.game_name}</p>
                                    <p className="text-sm text-muted-foreground">{data.game_date}</p>
                                    <p className="text-primary font-semibold">{data.points} points</p>
                                    <p className={data.result === 'win' ? 'text-green-600' : 'text-red-600'}>
                                      {data.result === 'win' ? 'W' : 'L'} ({data.points} - {data.opponent_score})
                                    </p>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="points" 
                            stroke="#3b82f6" 
                            fill="#3b82f680" 
                            strokeWidth={2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="text-center py-12 text-muted-foreground">
                        <BarChart3 className="h-16 w-16 mx-auto opacity-50 mb-4" />
                        <p>No game data available yet</p>
                        <p className="text-sm">Complete some games on the Stats page to see trends</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Win/Loss Streak & Stats Comparison */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Win Percentage Trend */}
                  <Card className="border-2">
                    <CardHeader className="border-b bg-muted/30">
                      <CardTitle className="font-heading uppercase text-xl flex items-center gap-2">
                        <Trophy className="h-5 w-5" />
                        Win Percentage Trend
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      {seasonTrends?.games?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                          <LineChart data={seasonTrends.games}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="game_name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                            <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                            <Tooltip formatter={(value) => [`${value}%`, 'Win %']} />
                            <Line 
                              type="monotone" 
                              dataKey="win_percentage" 
                              stroke="#22c55e" 
                              strokeWidth={3}
                              dot={{ fill: '#22c55e', strokeWidth: 2 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <p>No data available</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Game Results */}
                  <Card className="border-2">
                    <CardHeader className="border-b bg-muted/30">
                      <CardTitle className="font-heading uppercase text-xl flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Recent Games
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      {seasonTrends?.games?.length > 0 ? (
                        <div className="space-y-2 max-h-[250px] overflow-y-auto">
                          {[...seasonTrends.games].reverse().slice(0, 10).map((game, idx) => (
                            <div 
                              key={idx} 
                              className={`flex items-center justify-between p-3 rounded-lg ${
                                game.result === 'win' ? 'bg-green-50 border-l-4 border-green-500' : 'bg-red-50 border-l-4 border-red-500'
                              }`}
                            >
                              <div>
                                <p className="font-semibold text-sm">{game.game_name}</p>
                                <p className="text-xs text-muted-foreground">{game.game_date}</p>
                              </div>
                              <div className="text-right">
                                <p className={`font-bold ${game.result === 'win' ? 'text-green-600' : 'text-red-600'}`}>
                                  {game.result === 'win' ? 'W' : 'L'}
                                </p>
                                <p className="text-sm">{game.points} - {game.opponent_score}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <p>No games recorded yet</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Stats Breakdown Chart */}
                <Card className="border-2">
                  <CardHeader className="border-b bg-muted/30">
                    <CardTitle className="font-heading uppercase text-xl flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Team Performance by Game
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    {seasonTrends?.games?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={seasonTrends.games}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="game_name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="points" name="Points" fill="#3b82f6" />
                          <Bar dataKey="rebounds" name="Rebounds" fill="#22c55e" />
                          <Bar dataKey="assists" name="Assists" fill="#a855f7" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="text-center py-12 text-muted-foreground">
                        <BarChart3 className="h-16 w-16 mx-auto opacity-50 mb-4" />
                        <p>No game statistics available</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Attendance Trend */}
                {seasonTrends?.attendance_trend?.length > 0 && (
                  <Card className="border-2">
                    <CardHeader className="border-b bg-muted/30">
                      <CardTitle className="font-heading uppercase text-xl flex items-center gap-2">
                        <UserCheck className="h-5 w-5" />
                        Attendance Trend
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={seasonTrends.attendance_trend}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                          <Tooltip formatter={(value) => [`${value}%`, 'Attendance']} />
                          <Line 
                            type="monotone" 
                            dataKey="attendance_rate" 
                            stroke="#f59e0b" 
                            strokeWidth={2}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
        </>
        )}
      </div>
    </Layout>
  );
}
